import { Fragment, useCallback, useMemo } from 'react';

import { formatCostInCents, getCTR, toCSV } from 'utils/clickme';
import { FILE_DATE_WITH_TIME_FORMAT, VIEW_DATE_FORMAT } from 'utils/constants/date';
import { formatDate } from 'utils/date';

import Button, { ButtonKind } from 'bloko/blocks/button';
import { FormSpacer } from 'bloko/blocks/form';

import useTrls from 'hooks/translation/useTrls';

import FilterWrapper from 'components/Filter';
import InputWithMultiDatePicker from 'components/InputWithMultiDatePicker';

import fetchStatistics, { getStatisticsMethod } from 'components/Statistic/Filter/fetchStatistics';

import { setStatisticsDates } from 'store/models/statistics';
import { useDispatch, useSelector } from 'store/utils';

const FILE_TYPE_XLSX = 'XLSX';

const getStatItem = (summary, data, showCr, showResponsesAndCr, trl, headers) => {
    const sources = {};
    headers.forEach((source) => {
        sources[source] = data[source] || 0;
    });
    const stat = {
        ...sources,
        [`${trl('statistic.Filter.Header.lvl1.charges')}`]: summary.charges || 0,
        [`${trl('statistic.Filter.Header.lvl1.shows')}`]: summary.impressions || 0,
        [`${trl('statistic.Filter.Header.lvl1.clicks')}`]: summary.clicks || 0,
        [`${trl('statistic.Filter.Header.lvl1.ctr')}`]: getCTR(summary.ctr),
    };
    if (showResponsesAndCr) {
        stat[`${trl('statistic.Filter.Header.lvl1.responses')}`] = data.RESPONSES || 0;
        if (showCr) {
            stat.cr = getCTR(summary.cr);
        }
    }
    if (summary?.averagePricePerClickInCents) {
        stat[`${trl('statistic.Filter.Header.lvl1.avgPricePerClick')}`] =
            formatCostInCents(summary.averagePricePerClickInCents) || 0;
    }
    return stat;
};

const Filter = () => {
    const { from, to, items, campaignId, bannerId, showCr, showResponsesAndCr, headers } = useSelector(
        (state) => state.statistics
    );
    const dispatch = useDispatch();
    const trl = useTrls();

    const CSVHeaderTranslationMap = useMemo(() => {
        const BASE_TRANSLATION = 'banner.actions.social.source.';
        return Object.fromEntries(headers.map((header) => [header, trl(`${BASE_TRANSLATION}${header}`)]));
    }, [headers, trl]);

    const onCsvClick = useCallback(() => {
        const date = formatDate(new Date(), FILE_DATE_WITH_TIME_FORMAT);
        const entity = bannerId > 0 ? `banner_${bannerId}` : `campaign_${campaignId}`;
        const headersCSV = [
            trl('statistic.Filter.Header.lvl1.date'),
            trl('statistic.Filter.Header.lvl1.shows'),
            trl('statistic.Filter.Header.lvl1.clicks'),
            ...Object.values(CSVHeaderTranslationMap),
            trl('statistic.Filter.Header.lvl1.ctr'),
        ];
        if (showResponsesAndCr) {
            headersCSV.push(trl('statistic.Filter.Header.lvl1.responses'));
            if (showCr) {
                headersCSV.push('cr');
            }
        }
        headersCSV.push(
            trl('statistic.Filter.Header.lvl1.avgPricePerClick'),
            trl('statistic.Filter.Header.lvl1.charges')
        );
        let stats = [];
        if (items?.stats) {
            stats = items.stats.map(({ date, summary, data }) => {
                return {
                    date: formatDate(date, VIEW_DATE_FORMAT),
                    ...getStatItem(summary, data, showCr, showResponsesAndCr, trl, headers),
                };
            });
        }
        if (items?.total) {
            const { summary, data } = items.total;
            stats.unshift({
                date: trl('statistic.Total'),
                ...getStatItem(summary, data, showCr, showResponsesAndCr, trl, headers),
            });
        }

        const formattedStats = stats.map((stat) =>
            Object.entries(stat).reduce((result, [key, value]) => {
                const translatedKey =
                    key === 'date' ? trl('statistic.Filter.Header.lvl1.date') : CSVHeaderTranslationMap[key] ?? key;
                result[translatedKey] = value;
                return result;
            }, {})
        );

        toCSV(`stats_${entity}_${date}.csv`, formattedStats, headersCSV, []);
    }, [bannerId, campaignId, items, showCr, showResponsesAndCr, trl, headers, CSVHeaderTranslationMap]);

    return (
        <FilterWrapper
            settings={
                <InputWithMultiDatePicker
                    action={(from, to) => dispatch(setStatisticsDates({ from, to }))}
                    from={from}
                    to={to}
                    dataQa={'calendar'}
                />
            }
            actions={
                <Fragment>
                    <Button
                        Element="a"
                        kind={ButtonKind.Success}
                        href={getStatisticsMethod({ from, to, bannerId, campaignId, type: FILE_TYPE_XLSX })}
                        data-qa={'xls_button'}
                    >
                        {trl('statistic.Filter.xls')}
                    </Button>
                    <FormSpacer>
                        <Button kind={ButtonKind.Success} onClick={onCsvClick} data-qa={'csv_button'}>
                            {trl('statistic.Filter.csv')}
                        </Button>
                    </FormSpacer>
                    <FormSpacer>
                        <Button
                            kind={ButtonKind.Success}
                            onClick={() => dispatch(fetchStatistics())}
                            data-qa={'find_button'}
                        >
                            {trl('statistic.Filter.find')}
                        </Button>
                    </FormSpacer>
                </Fragment>
            }
        />
    );
};

export default Filter;
